import { useEffect, useState } from "react"
import { CheckCircle2, EyeOff, AlertTriangle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/modules/shared/ui/components/card"
import { RadioGroup, RadioGroupItem } from "@/modules/shared/ui/components/radio-group"
import { Label } from "@/modules/shared/ui/components/label"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Button } from "@/modules/shared/ui/components/button"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { ScorecardCriterionInput } from "@/modules/recruitment/applications/ui/components/ScorecardCriterionInput"
import {
  RECOMMENDATION_LABELS,
  RECOMMENDATION_STYLES,
} from "@/modules/recruitment/applications/ui/components/scorecard-styles"
import { ScorecardRecommendation } from "@/modules/recruitment/enums/ScorecardRecommendation"
import { ScorecardStatus } from "@/modules/recruitment/enums/ScorecardStatus"
import {
  useScorecardTemplate,
  useMyScorecard,
  useSaveScorecardDraft,
  useSubmitScorecard,
} from "@/modules/recruitment/hooks/useScorecards"

interface RatingState {
  score: number | null
  evidence: string
}

interface ScorecardFormProps {
  organizationId: string
  applicationId: string
  vacancyId: string
  readOnly?: boolean
}

export function ScorecardForm({ organizationId, applicationId, vacancyId, readOnly = false }: ScorecardFormProps) {
  const { data: template, isLoading: isTemplateLoading } = useScorecardTemplate(organizationId, vacancyId)
  const { data: myScorecard, isLoading: isScorecardLoading } = useMyScorecard(organizationId, applicationId)
  const saveDraft = useSaveScorecardDraft(organizationId, applicationId)
  const submit = useSubmitScorecard(organizationId, applicationId)

  const [ratings, setRatings] = useState<Record<string, RatingState>>({})
  const [recommendation, setRecommendation] = useState<ScorecardRecommendation | null>(null)
  const [overallNotes, setOverallNotes] = useState("")

  useEffect(() => {
    if (!myScorecard) return
    setRatings(
      Object.fromEntries(
        myScorecard.ratings.map((r) => [r.criterionId, { score: r.score ?? null, evidence: r.evidence ?? "" }]),
      ),
    )
    setRecommendation(myScorecard.recommendation ?? null)
    setOverallNotes(myScorecard.overallNotes ?? "")
  }, [myScorecard])

  if (isTemplateLoading || isScorecardLoading) {
    return <p className="text-sm text-muted-foreground py-6">Loading scorecard&hellip;</p>
  }

  if (!template) {
    return (
      <Alert>
        <AlertDescription>
          No scorecard template has been configured for this role yet. Ask an organisation owner
          to set one up from the vacancy page.
        </AlertDescription>
      </Alert>
    )
  }

  const isSubmitted = myScorecard?.status === ScorecardStatus.Submitted
  const isStale = isSubmitted && (myScorecard?.isStale ?? false)
  const isLocked = isSubmitted && !isStale
  const isSaving = saveDraft.isPending || submit.isPending

  const criteria = template.criteria.slice().sort((a, b) => a.orderIndex - b.orderIndex)
  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0)
  const completedCount = criteria.filter(
    (c) => ratings[c.id]?.score && ratings[c.id]?.evidence?.trim(),
  ).length

  const buildRatingsPayload = () =>
    criteria.map((c) => ({
      criterionId: c.id,
      score: ratings[c.id]?.score ?? null,
      evidence: ratings[c.id]?.evidence ?? null,
    }))

  const handleSaveDraft = () => {
    saveDraft.mutate({
      ratings: buildRatingsPayload(),
      recommendation,
      overallNotes: overallNotes || null,
    })
  }

  const handleSubmit = () => {
    if (completedCount < criteria.length) {
      toast.error("Please rate and provide evidence for every criterion before submitting.")
      return
    }
    if (!recommendation) {
      toast.error("Please select an overall recommendation.")
      return
    }

    submit.mutate({
      ratings: buildRatingsPayload(),
      recommendation,
      overallNotes: overallNotes || null,
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Your scorecard</CardTitle>
          {!isLocked && (
            <p className="text-sm text-muted-foreground mt-1">
              {completedCount} of {criteria.length} criteria rated
            </p>
          )}
        </div>
        {isLocked && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900/50">
            <CheckCircle2 className="size-3.5" />
            Submitted
          </span>
        )}
        {isStale && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50">
            <AlertTriangle className="size-3.5" />
            Outdated — please resubmit
          </span>
        )}
      </CardHeader>

      <CardContent>
        {isLocked ? (
          <Alert className="mb-2">
            <CheckCircle2 className="size-4" />
            <AlertDescription>
              You submitted this scorecard
              {myScorecard?.submittedAt
                ? ` on ${new Date(myScorecard.submittedAt).toLocaleDateString()}`
                : ""}
              . It can no longer be edited.
            </AlertDescription>
          </Alert>
        ) : isStale ? (
          <Alert className="mb-2 border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10">
            <AlertTriangle className="size-4 text-amber-600 dark:text-amber-500" />
            <AlertDescription>
              The scorecard template was updated after you submitted
              {myScorecard?.submittedAt
                ? ` on ${new Date(myScorecard.submittedAt).toLocaleDateString()}`
                : ""}
              . Please review your ratings and resubmit.
            </AlertDescription>
          </Alert>
        ) : readOnly ? (
          <Alert className="mb-2">
            <EyeOff className="size-4" />
            <AlertDescription>
              You have read-only access to this candidate and can&apos;t submit a scorecard.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="mb-2">
            <EyeOff className="size-4" />
            <AlertDescription>
              Other panellists&apos; scores stay hidden until you submit yours.
            </AlertDescription>
          </Alert>
        )}

        <div>
          {criteria.map((criterion) => (
            <ScorecardCriterionInput
              key={criterion.id}
              criterion={criterion}
              weightShare={totalWeight > 0 ? Math.round((criterion.weight / totalWeight) * 100) : 0}
              score={ratings[criterion.id]?.score ?? null}
              evidence={ratings[criterion.id]?.evidence ?? ""}
              disabled={isLocked || readOnly}
              onScoreChange={(score) =>
                setRatings((prev) => ({
                  ...prev,
                  [criterion.id]: { score, evidence: prev[criterion.id]?.evidence ?? "" },
                }))
              }
              onEvidenceChange={(evidence) =>
                setRatings((prev) => ({
                  ...prev,
                  [criterion.id]: { evidence, score: prev[criterion.id]?.score ?? null },
                }))
              }
            />
          ))}
        </div>

        <div className="pt-5 space-y-3">
          <Label className="font-medium text-sm">Overall recommendation</Label>
          <RadioGroup
            value={recommendation ?? undefined}
            onValueChange={(value) => setRecommendation(value as ScorecardRecommendation)}
            disabled={isSubmitted || readOnly}
            className="flex flex-row flex-wrap gap-2"
          >
            {Object.values(ScorecardRecommendation).map((value) => {
              const isSelected = recommendation === value
              return (
                <div key={value}>
                  <RadioGroupItem
                    value={value}
                    id={`recommendation-${value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`recommendation-${value}`}
                    className={cn(
                      "flex h-9 items-center rounded-full border px-4 text-sm font-medium transition-colors",
                      "peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50",
                      isLocked || readOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                      isSelected
                        ? RECOMMENDATION_STYLES[value]
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {RECOMMENDATION_LABELS[value]}
                  </Label>
                </div>
              )
            })}
          </RadioGroup>

          <div className="space-y-2 pt-2">
            <Label className="font-medium text-sm">Overall notes (optional)</Label>
            <Textarea
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              disabled={isLocked || readOnly}
              placeholder="Anything the panel should know that doesn't fit a single criterion."
              className="min-h-16"
            />
          </div>
        </div>

        {!isLocked && !readOnly && (
          <div className="flex items-center gap-2 pt-5 border-t border-border mt-5">
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isStale ? "Resubmit scorecard" : "Submit scorecard"}
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
              Save draft
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">
              Submitting is final &mdash; it can&apos;t be edited afterwards.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
